import React, { useEffect, useMemo, useState } from "react";
import "../styles/theme-dark.css";
import "./Maquinas.css";
const API = "/api";


/* ===============================
   Normalizador de texto
=============================== */
const norm = (s = "") =>
  (s + "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

/* ===============================
   Formatear para mostrar precios
=============================== */
const formatPrecio = (precio) => {
  const numero = Number(precio);
  if (isNaN(numero)) return "0,00 €";
  const partes = numero.toFixed(2).split(".");
  const entero = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${entero},${partes[1]} €`;
};

/* ===============================
  Formatear campo de entrada a formato europeo
=============================== */
const formatEuroInput = (value) => {
  if (value === "" || value === null) return "";
  let cleaned = value.toString().replace(/\./g, "").replace(",", ".");
  let num = parseFloat(cleaned);
  if (isNaN(num)) return "";
  return num.toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function Maquinas() {
  const [maquinas, setMaquinas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  // modal
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const emptyForm = {
    nombre: "",
    descripcion: "",
    precio: "",
  };
  const [form, setForm] = useState(emptyForm);

  /* ===============================
      Cargar máquinas
  ================================ */
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/maquinas`);
        const json = await res.json();
        setMaquinas(json.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ===============================
      Búsqueda / Filtrado
  ================================ */
  const filtered = useMemo(() => {
    if (!query.trim()) return maquinas;
    const q = norm(query);
    return maquinas.filter(
      (m) =>
        norm(m.nombre).includes(q) ||
        norm(m.descripcion || "").includes(q) ||
        String(m.precio).includes(query)
    );
  }, [maquinas, query]);

  /* ===============================
      Modal
  ================================ */
  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (m) => {
    setEditingId(m.id);
    setForm({
      nombre: m.nombre || "",
      descripcion: m.descripcion || "",
      precio: formatEuroInput(m.precio),
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (!saving) setShowModal(false);
  };

  const onChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const onChangePrecio = (e) => {
    const raw = e.target.value.replace(/\./g, "").replace(",", ".");
    setForm((prev) => ({
      ...prev,
      precio: e.target.value, // se sigue mostrando formateado
      precioRaw: raw === "" ? "" : Number(raw),
    }));
  };

  /* ===============================
      Crear máquina
  ================================ */
  const createMachine = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        nombre: form.nombre,
        descripcion: form.descripcion,
        precio: form.precioRaw || 0,
      };

      const res = await fetch(`${API}/maquinas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();

      if (json?.success) {
        setMaquinas((prev) => [json.data, ...prev]);
        setShowModal(false);
      } else alert("No se pudo crear la máquina");
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  /* ===============================
      Guardar cambios
  ================================ */
  const saveChanges = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        nombre: form.nombre,
        descripcion: form.descripcion,
        precio: form.precioRaw || 0,
      };

      const res = await fetch(`${API}/maquinas/${editingId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();

      if (json?.success) {
        setMaquinas((prev) =>
          prev.map((x) => (String(x.id) === String(editingId) ? json.data : x))
        );
        setShowModal(false);
      } else alert("No se pudo guardar");
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  /* ===============================
      Eliminar
  ================================ */
  const removeMachine = async (id) => {
    if (!confirm("¿Eliminar esta máquina?")) return;
    try {
      const res = await fetch(`${API}/maquinas/${id}`, { method: "DELETE" });
      const json = await res.json();

      if (json?.success) {
        setMaquinas((prev) => prev.filter((x) => String(x.id) !== String(id)));
      }
    } catch (e) {
      console.error(e);
    }
  };

  /* ===============================
      UI — DARK + GLASS
  ================================ */
  return (
    <div className="page-dark">
      {/* HEADER */}
      <header
        style={{
          padding: "20px 30px",
          marginBottom: 25,
          background: "rgba(15,23,42,0.65)",
          borderBottom: "1px solid rgba(255,255,255,0.12)",
          backdropFilter: "blur(10px)",
          borderRadius: "0 0 16px 16px",
          boxShadow: "0 10px 25px rgba(0,0,0,0.45)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img src="/corex-logo.png" alt="COREX" style={{ height: 40 }} />
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>
              Máquinas
            </h1>
          </div>

          <button
            className="glass-btn"
            onClick={() => window.history.back()}
            style={{ fontSize: 14 }}
          >
            ← Volver
          </button>
        </div>

        <p style={{ opacity: 0.7, marginTop: 6 }}>
          Catálogo de máquinas para presupuestos.
        </p>
      </header>

      {/* BÚSQUEDA */}
      <div
        className="glass-box"
        style={{
          marginBottom: 18,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 14,
        }}
      >
        <div style={{ flex: 1 }}>
          <input
            className="glass-input"
            type="search"
            placeholder="Buscar máquina…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div style={{ fontSize: 12, marginTop: 4, opacity: 0.6 }}>
            Total: <strong>{maquinas.length}</strong> — Coincidencias:{" "}
            <strong>{filtered.length}</strong>
          </div>
        </div>

        <button className="glass-btn" onClick={openCreate}>
          + Nueva máquina
        </button>
      </div>

      {/* GRID */}
      <section className="glass-box" style={{ padding: 20 }}>
        {loading ? (
          <div>Cargando catálogo…</div>
        ) : filtered.length === 0 ? (
          <p>No se encontraron máquinas.</p>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 16,
            }}
          >
            {filtered.map((m) => (
              <div
                key={m.id}
                className="glass-box"
                style={{ padding: 16, borderRadius: 14 }}
              >
                <h3 style={{ margin: "0 0 8px" }}>{m.nombre}</h3>

                <div
                  style={{
                    fontSize: 13,
                    opacity: 0.8,
                    minHeight: 50,
                    marginBottom: 10,
                  }}
                >
                  {m.descripcion
                    ? m.descripcion.substring(0, 120) +
                      (m.descripcion.length > 120 ? "…" : "")
                    : "Sin descripción"}
                </div>

                <div
                  style={{
                    fontWeight: 700,
                    marginBottom: 12,
                    color: "#3b82f6",
                  }}
                >
                  {formatPrecio(m.precio)}
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn-small" onClick={() => openEdit(m)}>
                    Editar
                  </button>
                  <button
                    className="btn-small delete"
                    onClick={() => removeMachine(m.id)}
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* MODAL */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 600 }}>
            <div
              className="modal-header"
              style={{
                borderBottom: "1px solid rgba(255,255,255,0.15)",
                marginBottom: 20,
              }}
            >
              <h2>
                {editingId == null ? "Nueva Máquina" : "Editar Máquina"}
              </h2>
              <button className="btn-small" onClick={closeModal}>
                ✕
              </button>
            </div>

            <form
              onSubmit={editingId == null ? createMachine : saveChanges}
              className="modal-body"
            >
              {/* NOMBRE */}
              <div>
                <label>Nombre *</label>
                <input
                  className="glass-input"
                  name="nombre"
                  value={form.nombre}
                  onChange={onChange}
                  required
                />
              </div>

              {/* DESCRIPCIÓN */}
              <div>
                <label>Descripción</label>
                <textarea
                  className="glass-input"
                  name="descripcion"
                  rows={4}
                  value={form.descripcion}
                  onChange={onChange}
                />
              </div>

              {/* PRECIO */}
              <div>
                <label>Precio sin IVA (€)</label>
                <input
                  className="glass-input"
                  type="text"
                  name="precio"
                  value={form.precio}
                  onChange={onChangePrecio}
                />
              </div>

              {/* FOOTER */}
              <div className="modal-footer">
                <button type="button" className="btn-small" onClick={closeModal}>
                  Cancelar
                </button>

                <button type="submit" className="btn-small">
                  {saving
                    ? "Guardando…"
                    : editingId == null
                    ? "Crear Máquina"
                    : "Guardar Cambios"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
